import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { clipsInSections, RECUT_CHIPS, type RecutChip } from 'fym-shared/src/music24/musicVideoDoc';
import { MUSIC_VIDEO_RECUT_TEXT_MAX_LENGTH } from 'fym-shared/src/music24/musicVideoApi';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { TypedMemo } from '../../common/types';
import { useShowToast } from '../../../redux/slices/toasts/hooks';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { RecutChips } from './RecutChips';
import { RecutSectionTools } from './RecutSectionTools';
import { timelineMessages } from '../messages/timelineMessages';
import { recutErrorText } from './recutErrorText';
import { formatTime } from './timelineGeometry';
import { useRecut } from './useRecut';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import editorStyles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */
import styles from './RecutBox.module.scss';

interface RecutBoxProps {
  target: number | 'all';
  onClose: () => void;
}

/**
 * The recut box of a section, or of the whole video (design c6): the chips, a free text, "Recut
 * <section> · no credits". For one section it also offers "Use this cut for every chorus" and the
 * cut length (`RecutSectionTools`). A recut costs no credits: it changes the document only.
 */
const RecutBoxComponent: React.FC<RecutBoxProps> = ({ target, onClose }) => {
  const intl = useIntl();
  const toast = useShowToast();
  const { songMap, doc } = useMusicVideoEditor();
  const [chips, setChips] = useState<RecutChip[]>([]);
  const [text, setText] = useState('');
  const { recut, isBusy, error, saveError } = useRecut();
  const errorText = recutErrorText(intl, error, saveError);
  const section = target === 'all' ? null : (songMap.sections.find((item) => item.index === target) ?? null);
  const clipCount = useMemo(
    () => (section === null ? doc.clips.length : clipsInSections(doc, songMap.sections, [section.index]).length),
    [doc, section, songMap.sections]
  );
  const from = section?.startSec ?? doc.settings.startSec;
  const to = section?.endSec ?? doc.settings.endSec;

  const start = () => {
    recut(section === null ? 'all' : [section.index], chips, text)
      .then((started) => {
        if (started) {
          toast.showInfo(intl.formatMessage(timelineMessages.recutStarted));
          onClose();
        }
      })
      .catch(console.error);
  };

  return (
    <div
      className={styles.recut}
      role="dialog"
      aria-label={section?.label ?? intl.formatMessage(timelineMessages.recutAllHeading)}
    >
      <h5>
        {section?.label ?? intl.formatMessage(timelineMessages.recutAllHeading)}
        <small>
          {intl.formatMessage(timelineMessages.recutFacts, {
            from: formatTime(from),
            to: formatTime(to),
            count: clipCount,
          })}
        </small>
        <button
          type="button"
          className={styles.recutClose}
          aria-label={intl.formatMessage(timelineMessages.close)}
          onClick={onClose}
        >
          ×
        </button>
      </h5>
      <span className={editorStyles.eyebrow}>{intl.formatMessage(timelineMessages.recutEyebrow)}</span>
      <RecutChips chips={RECUT_CHIPS} picked={chips} onChange={setChips} />
      <textarea
        className={editorStyles.textArea}
        value={text}
        maxLength={MUSIC_VIDEO_RECUT_TEXT_MAX_LENGTH}
        placeholder={intl.formatMessage(timelineMessages.recutPlaceholder)}
        onChange={(event) => setText(event.target.value)}
      />
      <SMLButton size="sm" omitCap stretch isLoading={isBusy} disabled={isBusy} onClick={start}>
        {section === null
          ? intl.formatMessage(timelineMessages.recutAllButton)
          : intl.formatMessage(timelineMessages.recutButton, { section: section.label })}
      </SMLButton>
      {errorText !== null && (
        <p className={editorStyles.errorText} role="alert">
          {errorText}
        </p>
      )}
      {section !== null && <RecutSectionTools section={section} onDone={onClose} />}
    </div>
  );
};

export const RecutBox = TypedMemo(RecutBoxComponent);
